<template>
  <div>
    <div v-if="pending">
      <div class="container my-5">
        <p>Loading...</p>
      </div>
    </div>
    <div v-else-if="error">
      <div class="container my-5">
        <p>Error: {{ error }}</p>
      </div>
    </div>
    <div v-else-if="data && !error">
      <LayoutPageHeader 
        v-if="headerImage"
        :title="frontmatter.title || 'Blog Post'" 
        :background-image="headerImage"
      />
      <main class="article">
        <div class="container">
          <article class="blog-post">
            <header class="mb-4">
              <h1 v-if="!frontmatter.heroImage && !frontmatter.image">{{ cleanTitle }}</h1>
              <div class="post-meta text-muted mb-3">
                <span v-if="frontmatter.date">
                  Published {{ formatDate(frontmatter.date) }}
                </span>
                <span v-if="hasUpdates" class="ms-3">
                  <i class="fas fa-sync-alt me-1"></i>
                  Updated <a href="#updates" class="text-decoration-none">{{ formatDate(lastUpdated) }}</a>
                </span>
                <span v-if="data.readingTime">
                  · {{ data.readingTime }}
                </span>
                <span v-if="data.authorDetails && data.authorDetails.length">
                  · by
                  <template v-for="(author, index) in data.authorDetails" :key="author.slug">
                    <NuxtLink :to="`/authors/${author.slug}`">{{ author.name }}</NuxtLink><template v-if="index < data.authorDetails.length - 1">, </template>
                  </template>
                </span>
              </div>
              <div v-if="frontmatter.category" class="category-badges mb-3">
                <template v-if="Array.isArray(frontmatter.category)">
                  <NuxtLink 
                    v-for="cat in frontmatter.category" 
                    :key="cat"
                    :to="`/blog/category/${cat}`"
                    class="category-badge"
                  >
                    {{ cat }}
                  </NuxtLink>
                </template>
                <template v-else>
                  <NuxtLink :to="`/blog/category/${frontmatter.category}`" class="category-badge">
                    {{ frontmatter.category }}
                  </NuxtLink>
                </template>
              </div>
              <p v-if="frontmatter.description" class="lead">{{ cleanDescription }}</p>
            </header>
            <div class="content" v-html="htmlContent"></div>
            
            <!-- Updates Section -->
            <div v-if="hasUpdates" id="updates" class="updates-section mt-5 pt-4 border-top">
              <h4 class="updates-title mb-4">
                <i class="fas fa-history me-2"></i>
                Post Updates
              </h4>
              <div class="updates-list">
                <div v-for="(update, index) in sortedUpdates" :key="index" class="update-item mb-3 p-3 bg-light rounded">
                  <div class="update-description text-secondary mb-2">
                    <i class="fas fa-edit me-2 text-primary"></i>
                    {{ update.description }}
                  </div>
                  <div class="update-date text-muted small">
                    <i class="fas fa-calendar-alt me-1"></i>
                    {{ formatDate(update.date) }}
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Comments Section -->
            <div class="comments-section mt-5 pt-4 border-top">
              <div v-if="commentsEnabled" class="comments-enabled">
                <h4 class="comments-title mb-3">
                  <i class="fas fa-comments me-2"></i>
                  Comments
                </h4>
                <p class="comments-message text-muted mb-3" v-html="commentsDisplayMessage" />
                <!-- <div class="alert alert-info" role="alert">
                  <i class="fas fa-info-circle me-2"></i>
                  Comments functionality coming soon! We're working on a great discussion experience for you.
                </div> -->
              </div>
              <div v-else class="comments-disabled">
                <div class="alert alert-secondary" role="alert">
                  <i class="fas fa-comment-slash me-2" />
                  <span v-html="commentsDisplayMessage" />
                </div>
              </div>
            </div>
            
            <!-- Previous/Next Navigation -->
            <div v-if="previousPost || nextPost" class="post-navigation mt-5 pt-4 border-top">
              <div class="row g-3">
                <div class="col-md-6">
                  <NuxtLink 
                    v-if="previousPost" 
                    :to="previousPost.url" 
                    class="nav-button nav-button-prev w-100"
                  >
                    <i class="fas fa-arrow-left me-2" />
                    <span>{{ previousPost.label }}</span>
                  </NuxtLink>
                </div>
                <div class="col-md-6">
                  <NuxtLink 
                    v-if="nextPost" 
                    :to="nextPost.url" 
                    class="nav-button nav-button-next w-100"
                  >
                    <span>{{ nextPost.label }}</span>
                    <i class="fas fa-arrow-right ms-2" />
                  </NuxtLink>
                </div>
              </div>
            </div>
            
            <div class="mt-5 text-center">
              <NuxtLink to="/blog" class="btn btn-outline-primary">
                <i class="fas fa-arrow-left me-2" /> Back to All Blogs
              </NuxtLink>
            </div>
          </article>
        </div>
      </main>
    </div>
    <div v-else>
      <div class="container my-5">
        <p>Not found.</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useRoute } from 'vue-router'
import { computed } from 'vue'
import { marked } from 'marked'
import hljs from 'highlight.js'

// Configure marked with syntax highlighting
marked.setOptions({
  highlight: function(code, lang) {
    if (lang && hljs.getLanguage(lang)) {
      try {
        return hljs.highlight(code, { language: lang }).value
      } catch {
        // Fall back to auto-detection if language-specific highlighting fails
      }
    }
    return hljs.highlightAuto(code).value
  }
})

const route = useRoute()
const slug = Array.isArray(route.params.slug) ? route.params.slug : [route.params.slug]
const computedPath = `blog/${slug.filter(Boolean).join('/')}`

const { data, pending, error } = await useAsyncData(`content-${computedPath}`, async () => {
  const fileData = await $fetch(`/api/content/${computedPath}`)

  if (!fileData || !fileData.content) {
    throw createError({ statusCode: 404, statusMessage: 'Page not found' })
  }

  // Custom frontmatter parser
  const parseFrontmatter = (fileContent) => {
    const lines = fileContent.split('\n')
    const fm = {}
    let frontmatterEnd = 0

    // Helper function to clean up quoted values
    const cleanValue = (value) => {
      if (typeof value !== 'string') return value
      const trimmed = value.trim()
      // Remove quotes if the entire string is quoted
      if ((trimmed.startsWith('"') && trimmed.endsWith('"')) || 
          (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
        return trimmed.slice(1, -1)
      }
      return trimmed
    }

    if (lines[0] === '---') {
      let currentKey = ''
      let isInArray = false
      let currentArrayItem = null
      let isInObject = false

      for (let i = 1; i < lines.length; i++) {
        const line = lines[i]
        const trimmed = line.trim()
        const leadingSpaces = line.length - line.trimStart().length

        if (line === '---') {
          frontmatterEnd = i + 1
          break
        }

        // Skip comment lines
        if (trimmed.startsWith('#')) {
          continue
        }

        // Handle regular key-value pairs (highest priority)
        if (trimmed.includes(':') && leadingSpaces === 0 && !isInArray && !isInObject) {
          const [key, ...valueParts] = trimmed.split(':')
          const value = valueParts.join(':').trim()
          const keyName = key.trim()
          
          if (value) {
            // Key-value pair on the same line
            fm[keyName] = cleanValue(value)
            currentKey = ''
            isInArray = false
            isInObject = false
          } else {
            // Key with value on next line(s)
            currentKey = keyName
            
            // Look ahead to determine the structure
            if (i + 1 < lines.length) {
              const nextLine = lines[i + 1]
              const nextTrimmed = nextLine.trim()
              const nextLeadingSpaces = nextLine.length - nextLine.trimStart().length
              
              if (nextTrimmed.startsWith('-') && nextLeadingSpaces === 2) {
                // Array
                fm[currentKey] = []
                isInArray = true
                isInObject = false
              } else if (nextLeadingSpaces >= 2 && nextTrimmed.includes(':')) {
                // Object
                fm[currentKey] = {}
                isInArray = false
                isInObject = true
              } else if (nextTrimmed && nextLeadingSpaces === 0) {
                // Simple value on next line
                fm[currentKey] = ''
                isInArray = false
                isInObject = false
              } else {
                // Unrecognized structure, assume object for safety
                fm[currentKey] = {}
                isInArray = false
                isInObject = true
              }
            }
          }
        }
        // Handle array items
        else if (trimmed.startsWith('-') && isInArray && leadingSpaces === 2) {
          // Save any pending array item before starting new one
          if (currentArrayItem && currentKey && Array.isArray(fm[currentKey])) {
            fm[currentKey].push(currentArrayItem)
          }
          
          const value = trimmed.slice(1).trim()
          if (value.includes(':')) {
            // Object in array
            const [objKey, ...objValueParts] = value.split(':')
            currentArrayItem = {}
            currentArrayItem[objKey.trim()] = cleanValue(objValueParts.join(':').trim())
            isInObject = true
          } else {
            // Simple string value
            if (currentKey && Array.isArray(fm[currentKey])) {
              fm[currentKey].push(cleanValue(value))
            }
            currentArrayItem = null
            isInObject = false
          }
        }
        // Handle nested properties (objects or object properties in arrays)
        else if (trimmed.includes(':') && leadingSpaces >= 2) {
          const [objKey, ...objValueParts] = trimmed.split(':')
          const objValue = objValueParts.join(':').trim()
          
          if (currentArrayItem) {
            // Property of object in array
            currentArrayItem[objKey.trim()] = cleanValue(objValue)
          } else if (currentKey && typeof fm[currentKey] === 'object' && !Array.isArray(fm[currentKey])) {
            // Property of regular object
            fm[currentKey][objKey.trim()] = cleanValue(objValue)
          }
        }
        // Handle simple values on their own line
        else if (leadingSpaces === 0 && !trimmed.includes(':') && !isInArray && !isInObject && currentKey) {
          fm[currentKey] = cleanValue(trimmed)
          currentKey = ''
        }
        // Reset state when we encounter a top-level key after being in nested structures
        else if (trimmed.includes(':') && leadingSpaces === 0 && (isInArray || isInObject)) {
          // Save any pending array item
          if (currentArrayItem && currentKey && Array.isArray(fm[currentKey])) {
            fm[currentKey].push(currentArrayItem)
            currentArrayItem = null
          }
          
          // Reset state and parse as new key-value pair
          isInArray = false
          isInObject = false
          const [key, ...valueParts] = trimmed.split(':')
          const value = valueParts.join(':').trim()
          const keyName = key.trim()
          
          if (value) {
            fm[keyName] = cleanValue(value)
            currentKey = ''
          } else {
            // Key with no value - need to look ahead to determine structure!
            currentKey = keyName
            
            // Look ahead to determine the structure
            if (i + 1 < lines.length) {
              const nextLine = lines[i + 1]
              const nextTrimmed = nextLine.trim()
              const nextLeadingSpaces = nextLine.length - nextLine.trimStart().length
              
              if (nextTrimmed.startsWith('-') && nextLeadingSpaces === 2) {
                // Array
                fm[currentKey] = []
                isInArray = true
              } else if (nextLeadingSpaces >= 2 && nextTrimmed.includes(':')) {
                // Object
                fm[currentKey] = {}
                isInObject = true
              } else if (nextTrimmed && nextLeadingSpaces === 0) {
                // Simple value on next line
                fm[currentKey] = ''
              } else {
                // Unrecognized structure, assume object for safety
                fm[currentKey] = {}
                isInObject = true
              }
            }
          }
        }
        
        // Reset object state when we encounter a top-level element after object properties
        else if (leadingSpaces === 0 && !trimmed.includes(':') && isInObject) {
          isInObject = false
          currentKey = ''
        }
      }
      
      // Save any pending array item at the end
      if (currentArrayItem && currentKey && Array.isArray(fm[currentKey])) {
        fm[currentKey].push(currentArrayItem)
      }
    }
    
    return { frontmatter: fm, contentStart: frontmatterEnd }
  }

  const { frontmatter, contentStart } = parseFrontmatter(fileData.content)

  // Fetch author details
  const authorDetails = []
  let authorSlugs = []

  // Handle primary author
  if (frontmatter.author && frontmatter.author.slug) {
    authorSlugs.push(frontmatter.author.slug)
  }

  // Handle co-authors array
  if (frontmatter.authors && Array.isArray(frontmatter.authors)) {
    authorSlugs = [...authorSlugs, ...frontmatter.authors]
  }

  // Remove duplicates and fetch details
  const uniqueAuthorSlugs = [...new Set(authorSlugs)]

  for (const authorSlug of uniqueAuthorSlugs) {
    try {
      const authorData = await $fetch(`/api/content/authors/${authorSlug}`)
      const { frontmatter: authorFm } = parseFrontmatter(authorData.content)
      authorDetails.push({ ...authorFm, slug: authorSlug })
    } catch {
      console.error(`Could not fetch author: ${authorSlug}`)
    }
  }

  const content = fileData.content.split('\n').slice(contentStart).join('\n').trim()
  const htmlContent = marked(content)

  // Calculate reading time (200 words per minute)
  const wordCount = content.split(/\s+/).filter(Boolean).length
  const readingTime = `${Math.ceil(wordCount / 200)} min read`

  return { frontmatter, htmlContent, content, authorDetails, readingTime }
})

// Handle case where data is null (e.g., fetch error)
if (!data.value) {
  throw createError({ statusCode: 404, statusMessage: 'Page not found' })
}

const { frontmatter, htmlContent } = data.value

// Comments configuration with defaults
const commentsEnabled = computed(() => {
  return frontmatter.commentsEnabled !== false; // Default to true unless explicitly false
});

const commentsDisplayMessage = computed(() => {
  if (frontmatter.commentsMessage) {
    return frontmatter.commentsMessage;
  }
  
  // Default message (comments not yet implemented)
  return "Comments are not available. Feel free to share your feedback on <a href='https://www.linkedin.com/in/ericwilson1229/' target='_blank' rel='noopener noreferrer'>LinkedIn</a> or connect with <a href='https://www.linkedin.com/company/geekcafe/' target='_blank' rel='noopener noreferrer'>Geek Cafe</a>.";
});

// Updates configuration
const lastUpdated = computed(() => {
  return frontmatter.lastUpdated;
});

const hasUpdates = computed(() => {
  return frontmatter.updates && frontmatter.updates.length > 0;
});

const sortedUpdates = computed(() => {
  if (!hasUpdates.value) return [];
  
  return [...frontmatter.updates].sort((a, b) => {
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });
});

const headerImage = computed(() => {
  // Use heroImage first, then fallback to image
  if (frontmatter.heroImage) {
    return frontmatter.heroImage;
  }
  if (frontmatter.image) {
    return frontmatter.image;
  }

  const categoryValue = frontmatter.category;
  const hasDevopsCategory = Array.isArray(categoryValue)
    ? categoryValue.includes('devops')
    : categoryValue === 'devops';

  if (hasDevopsCategory) {
    return '/assets/images/categories/devops.svg';
  }
  return '';
});

const cleanTitle = computed(() => {
  return frontmatter.title ? frontmatter.title.replace(/^"|"$/g, '') : ''
})

const cleanDescription = computed(() => {
  return frontmatter.description ? frontmatter.description.replace(/^"|"$/g, '') : ''
})

// Previous/Next navigation
const previousPost = computed(() => {
  if (!frontmatter.previous) return null;
  
  // Support both string and object format
  if (typeof frontmatter.previous === 'string') {
    return { url: frontmatter.previous, label: 'Previous' };
  }
  
  // Handle object format
  if (frontmatter.previous && typeof frontmatter.previous === 'object') {
    return {
      url: frontmatter.previous.url || '',
      label: frontmatter.previous.label || 'Previous'
    };
  }
  
  return null;
});

const nextPost = computed(() => {
  if (!frontmatter.next) return null;
  
  // Support both string and object format
  if (typeof frontmatter.next === 'string') {
    return { url: frontmatter.next, label: 'Next' };
  }
  
  // Handle object format
  if (frontmatter.next && typeof frontmatter.next === 'object') {
    return {
      url: frontmatter.next.url || '',
      label: frontmatter.next.label || 'Next'
    };
  }
  
  return null;
});

// Format date to MM/DD/YYYY
function formatDate(dateString) {
  if (!dateString) return '';
  
  const date = new Date(dateString)
  
  // Check if date is invalid
  if (isNaN(date.getTime())) {
    return dateString; // Return original string if date is invalid
  }
  
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC' // Treat the date as UTC to prevent timezone shifts
  })
}

// Get the full URL for the current page
const config = useRuntimeConfig()
const currentUrl = computed(() => `${config.public.siteUrl || 'https://geekcafe.com'}${route.fullPath}`)

// Get the absolute image URL for social sharing
const socialImage = computed(() => {
  // Use thumbnailImage first, then heroImage, then image for social sharing
  const socialImageField = frontmatter.thumbnailImage || frontmatter.heroImage || frontmatter.image;
  
  if (socialImageField) {
    // If image starts with http, use it as is, otherwise make it absolute
    if (socialImageField.startsWith('http')) {
      return socialImageField;
    }
    return `${config.public.siteUrl || 'https://geekcafe.com'}${socialImageField}`;
  }
  // Fallback to a default social image
  return `${config.public.siteUrl || 'https://geekcafe.com'}/images/placeholder.svg`;
});

// Set up SEO meta tags for social sharing
useSeoMeta({
  title: cleanTitle.value,
  description: cleanDescription.value,
  ogTitle: cleanTitle.value,
  ogDescription: cleanDescription.value,
  ogImage: socialImage.value,
  ogUrl: currentUrl.value,
  ogType: 'article',
  twitterCard: 'summary_large_image',
  twitterTitle: cleanTitle.value,
  twitterDescription: cleanDescription.value,
  twitterImage: socialImage.value,
})

// Additional article-specific meta tags
useHead({
  meta: [
    { property: 'article:published_time', content: frontmatter.date },
    { property: 'article:author', content: data.value.authorDetails?.[0]?.name || 'Geek Cafe' },
    { property: 'article:section', content: Array.isArray(frontmatter.category) ? (frontmatter.category[0] || 'Technology') : (frontmatter.category || 'Technology') },
    ...(frontmatter.tags || []).map(tag => ({ property: 'article:tag', content: tag }))
  ]
})

</script>

<style scoped>
.article {
  padding: 3rem 0;
}

.blog-post {
  max-width: 800px;
  margin: 0 auto;
}

.blog-post h1 {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #333;
}

.post-meta {
  font-size: 0.9rem;
  color: #666;
}

.post-meta a {
  color: #007bff;
  text-decoration: none;
}

.post-meta a:hover {
  text-decoration: underline;
}

.lead {
  font-size: 1.25rem;
  color: #666;
  margin-bottom: 2rem;
}

.content :deep(h2) {
  font-size: 2rem;
  font-weight: 600;
  margin-top: 2rem;
  margin-bottom: 1rem;
  color: #333;
}

.content :deep(h3) {
  font-size: 1.5rem;
  font-weight: 600;
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
  color: #333;
}

.content :deep(p) {
  margin-bottom: 1rem;
  line-height: 1.7;
  color: #555;
}

.content :deep(pre) {
  background: #f5f5f5;
  padding: 1rem;
  border-radius: 4px;
  overflow-x: auto;
  margin-bottom: 1rem;
}

.content :deep(code) {
  background: #f5f5f5;
  padding: 0.2rem 0.4rem;
  border-radius: 3px;
  font-size: 0.9em;
}

.content :deep(pre code) {
  background: transparent;
  padding: 0;
}

.content :deep(ul),
.content :deep(ol) {
  margin-bottom: 1rem;
  padding-left: 2rem;
}

.content :deep(li) {
  margin-bottom: 0.5rem;
  line-height: 1.7;
}

.content :deep(blockquote) {
  border-left: 4px solid #007bff;
  padding-left: 1rem;
  margin: 1.5rem 0;
  color: #666;
  font-style: italic;
}

.content :deep(img) {
  max-width: 100%;
  height: auto;
  margin: 1rem 0;
  border-radius: 4px;
}

.content :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 1.5rem 0;
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  overflow: hidden;
}

.content :deep(thead) {
  background: #f8f9fa;
}

.content :deep(th) {
  padding: 0.75rem 1rem;
  text-align: left;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #dee2e6;
}

.content :deep(td) {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #dee2e6;
  color: #555;
}

.content :deep(tbody tr:last-child td) {
  border-bottom: none;
}

.content :deep(tbody tr:hover) {
  background: #f8f9fa;
}

/* Mobile responsive tables */
@media (max-width: 768px) {
  .content :deep(table) {
    font-size: 0.9rem;
  }
  
  .content :deep(th),
  .content :deep(td) {
    padding: 0.5rem 0.75rem;
  }
}

/* Comments Section Styles */
.comments-section {
  margin-top: 3rem;
}

.comments-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
}

.comments-message {
  font-size: 1rem;
  line-height: 1.6;
}

.comments-enabled .alert-info {
  border-left: 4px solid #17a2b8;
}

.comments-disabled .alert-secondary {
  border-left: 4px solid #6c757d;
}

.comments-title i,
.comments-enabled .alert-info i,
.comments-disabled .alert-secondary i {
  opacity: 0.8;
}

/* Debug Info Styles */
.debug-info {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  padding: 1rem;
  margin-bottom: 1rem;
  border-radius: 4px;
  font-size: 0.8rem;
  overflow-x: auto;
}

/* Updates Section Styles */
.updates-section {
  margin-top: 3rem;
}

.updates-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
}

.updates-list {
  max-width: 100%;
}

.update-item {
  border-left: 3px solid #007bff;
  transition: all 0.2s ease;
}

.update-item:hover {
  border-left-color: #0056b3;
  background-color: #f8f9fa !important;
  transform: translateX(2px);
}

.update-description {
  font-size: 0.95rem;
  line-height: 1.6;
}

.update-date {
  font-size: 0.85rem;
  font-style: italic;
  opacity: 0.8;
}

/* Updated date link styling */
.post-meta a {
  color: #007bff;
  text-decoration: none;
  transition: color 0.2s;
}

.post-meta a:hover {
  color: #0056b3;
  text-decoration: underline;
}

/* Previous/Next Navigation Buttons */
.post-navigation {
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid #e0e0e0;
}

.nav-button {
  display: inline-flex;
  align-items: center;
  padding: 0.75rem 1.25rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 500;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.nav-button:hover {
  background: linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  transform: translateY(-2px);
  color: white;
}

.nav-button-prev {
  justify-content: flex-start;
}

.nav-button-next {
  justify-content: flex-end;
}

.nav-button i {
  font-size: 0.9rem;
}

/* Category Badges */
.category-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.category-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  text-decoration: none;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.category-badge:hover {
  background: linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%);
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(102, 126, 234, 0.3);
  color: white;
  text-decoration: none;
}
</style>
